import {
    Box,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    Tr,
    VStack,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { TbSearch, TbTrash } from 'react-icons/tb';
import {
    PARTBAY_BLUE,
    SettingsInlineEdit,
    SettingsOptionRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import { InlineCellDisplay } from '../../../../components/settings/UsersInlineCells';

/** Rows of carriers shown at once inside the editor panel. */
const CARRIER_PAGE_SIZE = 5;

export interface CarrierOption {
    id: string;
    name: string;
}

/** The carriers a note applies to, as stored: every carrier, or a fixed list. */
export type CarrierSelection = Array<string> | 'all';

export const formatCarrierSelection = (
    selection: CarrierSelection,
    options: CarrierOption[],
): string => {
    if (selection === 'all') return 'All';
    if (selection.length === 0) return 'None';
    if (options.length > 0 && selection.length === options.length) return 'All';
    if (selection.length === 1) {
        return options.find((option) => option.id === selection[0])?.name ?? '1 carrier';
    }
    return `${selection.length} carriers`;
};

/** Ids the selection resolves to against the org's carriers. */
const toCarrierIds = (selection: CarrierSelection, options: CarrierOption[]): string[] =>
    selection === 'all' ? options.map((option) => option.id) : [...selection];

const isSameSelection = (a: CarrierSelection, b: CarrierSelection, options: CarrierOption[]) => {
    const left = toCarrierIds(a, options);
    const right = new Set(toCarrierIds(b, options));
    return left.length === right.size && left.every((id) => right.has(id));
};

export interface NoteCarriersCellProps {
    value: CarrierSelection;
    options: CarrierOption[];
    onSave: (next: CarrierSelection) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Which carriers a system generated note applies to. Built the same way as a
 * DRP carrier's prohibited vendors: search to add one, remove it from the
 * paginated list underneath. A list covering every carrier is stored as `all`,
 * so the note keeps applying as carriers are added.
 */
const NoteCarriersCell = ({
    value,
    options,
    onSave,
    isDisabled = false,
    ariaLabel,
}: NoteCarriersCellProps) => {
    const [draft, setDraft] = useState<string[]>(() => toCarrierIds(value, options));
    const [search, setSearch] = useState('');

    const selected = useMemo(() => {
        const byId = new Map(options.map((option) => [option.id, option]));
        return draft.map((id) => byId.get(id) ?? { id, name: id });
    }, [draft, options]);

    // Already-selected carriers drop out of the results: they are shown, and
    // removable, in the list underneath.
    const results = useMemo(() => {
        const chosen = new Set(draft);
        const term = search.trim().toLowerCase();
        return options
            .filter((option) => !chosen.has(option.id))
            .filter((option) => (term ? option.name.toLowerCase().includes(term) : true));
    }, [draft, options, search]);

    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(
        selected,
        CARRIER_PAGE_SIZE,
    );

    const isSearching = search.trim().length > 0;

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatCarrierSelection(value, options)}
                    isMuted={value !== 'all' && value.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="280px"
            onOpen={() => {
                setDraft(toCarrierIds(value, options));
                setSearch('');
            }}
            onClose={() => {
                // A list covering every carrier collapses back to `all`, which
                // is what the note screen shows and what new carriers inherit.
                const next: CarrierSelection =
                    options.length > 0 && draft.length === options.length ? 'all' : draft;
                if (!isSameSelection(next, value, options)) onSave(next);
            }}
        >
            <VStack align="stretch" spacing={2}>
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        autoFocus
                        value={search}
                        placeholder="Search carriers to add"
                        onChange={(e) => setSearch(e.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>

                {isSearching && (
                    <Box
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        maxH="160px"
                        overflowY="auto"
                    >
                        {results.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" px={3} py={2}>
                                No carriers found
                            </Text>
                        ) : (
                            results.map((option) => (
                                <SettingsOptionRow
                                    key={option.id}
                                    label={option.name}
                                    isSelected={false}
                                    onToggle={() => {
                                        setDraft((prev) => [...prev, option.id]);
                                        setSearch('');
                                    }}
                                />
                            ))
                        )}
                    </Box>
                )}

                {options.length === 0 ? (
                    <Text fontSize="xs" color="gray.500">
                        No carriers available.
                    </Text>
                ) : selected.length === 0 ? (
                    <Text fontSize="xs" color="gray.500">
                        No carriers — this note is never applied for these jobs.
                    </Text>
                ) : (
                    <>
                        <SettingsSubTable
                            head={
                                <>
                                    <SettingsTh>CARRIER</SettingsTh>
                                    <SettingsTh w="44px" aria-label="Remove" />
                                </>
                            }
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        >
                            {pageItems.map((option) => (
                                <Tr key={option.id}>
                                    <SettingsTd>
                                        <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                            {option.name}
                                        </Text>
                                    </SettingsTd>
                                    <SettingsTd textAlign="right">
                                        <IconButton
                                            aria-label={`Remove ${option.name}`}
                                            icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() =>
                                                setDraft((prev) =>
                                                    prev.filter((id) => id !== option.id),
                                                )
                                            }
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))}
                        </SettingsSubTable>
                        <HStack justify="space-between" spacing={2}>
                            <Text fontSize="xs" color="gray.500">
                                {selected.length} of {options.length} carriers
                            </Text>
                            {selected.length < options.length && (
                                <Box
                                    as="button"
                                    type="button"
                                    fontSize="xs"
                                    fontWeight="medium"
                                    color={PARTBAY_BLUE}
                                    onClick={() => setDraft(options.map((option) => option.id))}
                                >
                                    Select all
                                </Box>
                            )}
                        </HStack>
                    </>
                )}
            </VStack>
        </SettingsInlineEdit>
    );
};

export default NoteCarriersCell;
